/**
 * Locations Panel Component (JSX)
 * Manages the display and CRUD operations for locations
 * Follows Single Responsibility and Dependency Inversion Principles
 * WITH CURSOR-BASED PAGINATION for scalability
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { LOCATIONS_QUERY_KEY } from '../../hooks/useLocationsInfinite';
import { LocationsSearchList } from './LocationsSearchList';
import { AddLocationInline } from './AddLocationInline';
import { BulkActionsBar } from './BulkActionsBar';
import { BulkAddLocationsModal } from './BulkAddLocationsModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { DuplicateLocationModal } from './DuplicateLocationModal';
import { CleaningStandardsDrawer } from '../standards/CleaningStandardsDrawerV2';
import { CopyFromLocationModal } from '../standards/CopyFromLocationModalV2';
import {
  putLocationCleaningStandards,
  patchLocationCleaningStandardsDraft,
  copyLocationCleaningStandards,
  getLocationCleaningStandards,
} from '../../services/locationCleaningStandardsService';
import {
  createLocation,
  createLocationInMultipleFacilities,
  getLocationsByFacility,
  getLocationsBulkStatus,
  duplicateLocation,
  updateLocation,
  deleteLocation,
  deleteMultipleLocations,
} from '../../services/locationsService';
import { getFacilitiesByOrganization } from '../../services/facilitiesService';
import { useFeedbackContext } from '../../context';
import { Check, Users, Sparkles } from 'lucide-react';
import {
  INFO_CARD_ICON_WRAPPER,
  INFO_CARD_ICON_CLASS,
} from '../standards/constants/infoCardStyles';
import { serviceLogger } from '../../utils/serviceLogger';

/** In Step 3 (standards mode), bulk actions bar is shown only when at least this many locations are selected */
const MIN_SELECTED_FOR_BULK_BAR_STANDARDS_MODE = 2;

/**
 * @param {{
 *  selectedFacilityIds: string[];
 *  organizationId: string;
 *  queryScope?: string; Isolates cache per context (e.g. 'organization-structure' vs 'cleaning-standards') so pagination in one step does not affect the other
 *  onLocationsChange?: () => void;
 *  readOnlyMode?: boolean;
 *  showCleaningStandards?: boolean;
 * }} props
 */
export const LocationsPanel = ({
  selectedFacilityIds,
  organizationId,
  queryScope = 'default',
  onLocationsChange,
  readOnlyMode = false,
  showCleaningStandards = false,
}) => {
  const { showToast } = useFeedbackContext();
  const queryClient = useQueryClient();
  const scope = typeof queryScope === 'string' && queryScope ? queryScope : 'default';

  // Get the single selected facility
  const selectedFacilityId = selectedFacilityIds.length === 1 ? selectedFacilityIds[0] : null;

  // Locations list: updated by LocationsSearchList via onLocationsChange (isolates search re-renders)
  const [locationsFromList, setLocationsFromList] = useState([]);

  const locationIds = useMemo(
    () => locationsFromList.map((loc) => loc.id).sort(),
    [locationsFromList],
  );

  // Step 1 (Set Organization Structure): do NOT call bulk-status. Step 3 (Cleaning Standards): call it.
  const { data: bulkStatusResponse } = useQuery({
    queryKey: ['locations-bulk-status', selectedFacilityId, locationIds.join(',')],
    queryFn: () => getLocationsBulkStatus(locationIds),
    enabled: showCleaningStandards && !!selectedFacilityId && locationIds.length > 0,
    staleTime: 1000 * 60 * 2,
  });

  const cleaningStandardsStatusByLocationId = useMemo(() => {
    const map = {};
    (bulkStatusResponse?.data ?? []).forEach((item) => {
      map[item.locationId] = item.cleaningStandardsStatus;
    });
    return map;
  }, [bulkStatusResponse]);

  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showStandardsDrawer, setShowStandardsDrawer] = useState(false);
  const [showCopyFromModal, setShowCopyFromModal] = useState(false);
  const [lastCopyResult, setLastCopyResult] = useState(null);
  const [initialStandardsForDrawer, setInitialStandardsForDrawer] = useState(null); // Full config to show after copy
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [currentLocationId, setCurrentLocationId] = useState(null); // For individual drawer
  const [locationToDuplicate, setLocationToDuplicate] = useState(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  // Track which locations have configured standards
  const [locationsWithStandards, setLocationsWithStandards] = useState(new Set());

  // Full list for drawer: when standards drawer is open, fetch ALL locations for the facility so "Copy FROM another area" considers every location (not just the current page)
  const { data: fullLocationsResponse } = useQuery({
    queryKey: ['locations-full', selectedFacilityId],
    queryFn: () => getLocationsByFacility(selectedFacilityId),
    enabled: !!selectedFacilityId && showStandardsDrawer,
    staleTime: 1000 * 60 * 2,
  });

  const fullLocationsList = useMemo(
    () => fullLocationsResponse?.data ?? fullLocationsResponse ?? [],
    [fullLocationsResponse],
  );

  // Stable callback so LocationsSearchList can notify without causing unnecessary re-renders
  const handleLocationsChange = useCallback((list) => {
    setLocationsFromList(list ?? []);
  }, []);

  // EDGE CASE: Clear standards state when facility changes
  useEffect(() => {
    serviceLogger.log('[EDGE CASE] Facility changed, clearing standards state');
    setLocationsWithStandards(new Set());
    setSelectedLocationIds([]);
    setLocationsFromList([]);
  }, [selectedFacilityId]);

  // Update locationsWithStandards: use bulk-status API when available ("set" = configured), else list data
  const configuredStandards = useMemo(() => {
    if (locationsFromList.length === 0) return new Set();

    const configured = new Set();
    locationsFromList.forEach((loc) => {
      const status = cleaningStandardsStatusByLocationId[loc.id] ?? loc.cleaningStandardsStatus;
      if (status === 'set' || (status === 'assigned' && loc.cleaningStandards)) {
        configured.add(loc.id);
      }
    });
    return configured;
  }, [locationsFromList, cleaningStandardsStatusByLocationId]);

  // Only update state when the set actually changes
  useEffect(() => {
    const configuredArray = Array.from(configuredStandards);
    const currentArray = Array.from(locationsWithStandards);
    const hasChanged =
      configuredArray.length !== currentArray.length ||
      !configuredArray.every((id) => currentArray.includes(id));

    if (hasChanged) {
      setLocationsWithStandards(configuredStandards);
      serviceLogger.log('[LocationsPanel] Updated configured standards:', configuredArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredStandards]);

  // Load facilities data when multiple facilities are selected
  useEffect(() => {
    const loadFacilities = async () => {
      if (selectedFacilityIds.length > 1) {
        try {
          const allFacilities = await getFacilitiesByOrganization(organizationId);
          const selected = allFacilities.filter((f) => selectedFacilityIds.includes(f.id));
          setFacilities(selected);
        } catch (error) {
          serviceLogger.error('Error loading facilities:', error);
        }
      }
    };

    loadFacilities();
  }, [selectedFacilityIds, organizationId]);

  const handleAddLocation = async (name) => {
    // Handle single facility selection
    if (selectedFacilityIds.length === 1) {
      try {
        setIsAdding(true);
        const response = await createLocation(selectedFacilityIds[0], { name });
        const newLocation = response.data;

        // Invalidate locations query so LocationsSearchList refetches
        await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });

        showToast({
          variant: 'success',
          title: 'Success',
          copy: `Area added successfully. ${newLocation.name} has been added`,
        });

        onLocationsChange?.();
      } catch (error) {
        showToast({
          variant: 'error',
          title: 'Error',
          copy: error.message || 'Failed to add area',
        });
        throw error;
      } finally {
        setIsAdding(false);
      }
      return;
    }

    // Handle multiple facilities selection (optimized batch creation)
    if (selectedFacilityIds.length > 1) {
      try {
        setIsAdding(true);
        const results = await createLocationInMultipleFacilities(
          selectedFacilityIds,
          { name },
          3, // concurrency limit
        );

        const successCount = results.length;
        const failedCount = selectedFacilityIds.length - successCount;

        // Invalidate cache for all affected facilities
        await Promise.all(
          selectedFacilityIds.map((facilityId) =>
            queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, facilityId] }),
          ),
        );

        // Refetch if we have a single facility selected (for display)
        if (selectedFacilityId) {
          await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });
        }

        let message = `Area "${name}" added to ${successCount} facilit${successCount !== 1 ? 'ies' : 'y'}`;
        if (failedCount > 0) {
          message += `. ${failedCount} failed`;
        }

        showToast({
          variant: successCount > 0 ? 'success' : 'error',
          title: successCount > 0 ? 'Success' : 'Error',
          copy: message,
          duration: 5000,
        });

        onLocationsChange?.();
      } catch (error) {
        showToast({
          variant: 'error',
          title: 'Error',
          copy: error.message || 'Failed to add area to some facilities',
        });
        throw error;
      } finally {
        setIsAdding(false);
      }
      return;
    }
  };

  const handleUpdateLocation = async (locationId, newName) => {
    if (!selectedFacilityId) return;

    try {
      await updateLocation({ id: locationId, name: newName });
      await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });
      showToast({ variant: 'success', title: 'Success', copy: 'Area updated successfully' });
    } catch (error) {
      showToast({ variant: 'error', title: 'Error', copy: 'Failed to update area' });
      throw error;
    }
  };

  const handleDuplicateLocation = (location) => {
    setLocationToDuplicate(location);
    setIsDuplicateModalOpen(true);
  };

  const handleCloseDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setLocationToDuplicate(null);
  };

  const handleConfirmDuplicateLocation = async ({ newName, includeStandards }) => {
    if (!locationToDuplicate?.id) return;

    const facilityIdForList = locationToDuplicate.facilityId ?? selectedFacilityId;
    if (!facilityIdForList) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: 'Could not refresh areas list. Please refresh the page.',
      });
      return;
    }

    try {
      const response = await duplicateLocation(locationToDuplicate.id, {
        newName,
        includeStandards,
        facilityId: locationToDuplicate.facilityId,
      });
      const { newLocation, duplicatedStandards } = response.data;

      await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, facilityIdForList] });

      let copy = `Area "${newLocation.name}" created successfully.`;
      if (duplicatedStandards) copy += ' Cleaning standards were copied.';
      showToast({ variant: 'success', title: 'Success', copy });
      onLocationsChange?.();
      handleCloseDuplicateModal();
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error.message || 'Failed to duplicate area. New name must be unique within the facility.',
      });
      throw error;
    }
  };

  const handleSelectLocation = (locationId) => {
    setSelectedLocationIds((prevIds) => {
      if (prevIds.includes(locationId)) {
        return prevIds.filter((id) => id !== locationId);
      }
      return [...prevIds, locationId];
    });
  };

  const handleDeleteLocations = async () => {
    if (!selectedFacilityId || selectedLocationIds.length === 0) return;

    try {
      setIsDeleting(true);
      if (selectedLocationIds.length === 1) {
        await deleteLocation(selectedLocationIds[0]);
      } else {
        await deleteMultipleLocations(selectedLocationIds);
      }

      // EDGE CASE: Remove deleted locations from standards tracking
      setLocationsWithStandards((prev) => {
        const updated = new Set(prev);
        selectedLocationIds.forEach((id) => updated.delete(id));
        serviceLogger.log(
          '[EDGE CASE] Removed deleted locations from standards:',
          selectedLocationIds,
        );
        return updated;
      });

      setShowDeleteModal(false);
      setSelectedLocationIds([]);
      await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });

      const count = selectedLocationIds.length;
      showToast({
        variant: 'success',
        title: 'Success',
        copy: `${count} area${count > 1 ? 's' : ''} deleted successfully`,
      });
      onLocationsChange?.();
    } catch (error) {
      showToast({ variant: 'error', title: 'Error', copy: 'Failed to delete areas' });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleClearSelection = () => setSelectedLocationIds([]);

  const handleSelectAllLocations = useCallback((displayedIds = []) => {
    setSelectedLocationIds((prev) => {
      const ids = Array.isArray(displayedIds) ? displayedIds : [];
      if (ids.length === 0) return prev;
      const allDisplayedSelected =
        prev.length === ids.length && ids.every((id) => prev.includes(id));
      return allDisplayedSelected ? [] : ids;
    });
  }, []);

  const handleOpenStandardsDrawer = () => setShowStandardsDrawer(true);
  // handleCloseStandardsDrawer is used inline in multiple places, keeping for consistency
  // eslint-disable-next-line no-unused-vars
  const handleCloseStandardsDrawer = () => setShowStandardsDrawer(false);

  const handleSaveStandards = async (standards) => {
    const targetLocationIds = currentLocationId ? [currentLocationId] : selectedLocationIds;
    if (targetLocationIds.length === 0) {
      showToast({ variant: 'error', title: 'Error', copy: 'No areas selected' });
      return;
    }

    try {
      // PUT /cleaning-standards/location/{locationId} (UPSERT) per location — parallel, senior approach
      const results = await Promise.allSettled(
        targetLocationIds.map((locationId) =>
          putLocationCleaningStandards(locationId, standards),
        ),
      );

      const fulfilled = results.filter((r) => r.status === 'fulfilled');
      const rejected = results.filter((r) => r.status === 'rejected');
      const successIds = targetLocationIds.filter((_, i) => results[i].status === 'fulfilled');

      if (successIds.length > 0) {
        setLocationsWithStandards((prev) => {
          const updated = new Set(prev);
          successIds.forEach((id) => updated.add(id));
          return updated;
        });
        await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });
        queryClient.invalidateQueries({ queryKey: ['locations-bulk-status'] });
        queryClient.invalidateQueries({ queryKey: ['locations-full', selectedFacilityId] });
      }

      if (rejected.length === 0) {
        showToast({
          variant: 'success',
          title: 'Success',
          copy: `Standards applied to ${targetLocationIds.length} area${
            targetLocationIds.length !== 1 ? 's' : ''
          }`,
        });
        setShowStandardsDrawer(false);
        setCurrentLocationId(null);
        setSelectedLocationIds([]);
      } else if (fulfilled.length === 0) {
        const firstError = rejected[0].reason?.message || 'Unknown error';
        showToast({
          variant: 'error',
          title: 'Error',
          copy: firstError.length > 80 ? 'Failed to save cleaning standards' : firstError,
        });
      } else {
        showToast({
          variant: 'error',
          title: 'Partial success',
          copy: `Applied to ${fulfilled.length} of ${targetLocationIds.length} areas. ${rejected.length} failed.`,
          duration: 5000,
        });
        setShowStandardsDrawer(false);
        setCurrentLocationId(null);
        setSelectedLocationIds([]);
      }
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error?.message || 'Failed to save cleaning standards',
      });
    }
  };

  const handleSaveDraft = async (draftPayload) => {
    const targetLocationIds = currentLocationId ? [currentLocationId] : selectedLocationIds;
    if (targetLocationIds.length === 0) {
      showToast({ variant: 'error', title: 'Error', copy: 'No areas selected' });
      return;
    }
    try {
      await Promise.all(
        targetLocationIds.map((locationId) =>
          patchLocationCleaningStandardsDraft(locationId, draftPayload),
        ),
      );
      await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });
      queryClient.invalidateQueries({ queryKey: ['locations-bulk-status'] });
      queryClient.invalidateQueries({ queryKey: ['locations-full', selectedFacilityId] });
      showToast({
        variant: 'success',
        title: 'Progress saved',
        copy: `Draft saved for ${targetLocationIds.length} area${targetLocationIds.length !== 1 ? 's' : ''}. You can continue later.`,
        duration: 3000,
      });
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error?.message || 'Failed to save progress',
      });
    }
  };

  // Handle opening standards drawer for individual location (load existing standards so drawer shows them)
  const handleOpenIndividualStandards = async (locationId) => {
    setCurrentLocationId(locationId);
    setSelectedLocationIds([locationId]);
    setInitialStandardsForDrawer(null);
    try {
      const { data } = await getLocationCleaningStandards(locationId);
      if (data) setInitialStandardsForDrawer(data);
    } catch {
      setInitialStandardsForDrawer(null);
    }
    setShowStandardsDrawer(true);
  };

  const handleCloseIndividualStandards = () => {
    setShowStandardsDrawer(false);
    setCurrentLocationId(null);
    setSelectedLocationIds([]);
    setLastCopyResult(null);
    setInitialStandardsForDrawer(null);
  };

  const handleOpenBulkAddModal = () => setShowBulkAddModal(true);
  const handleCloseBulkAddModal = () => setShowBulkAddModal(false);

  const handleBulkAddSuccess = useCallback(async () => {
    handleCloseBulkAddModal();
    // Refetch locations for ALL affected facilities so lists update without page refresh
    await Promise.all(
      selectedFacilityIds.map((facilityId) =>
        queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, facilityId] }),
      ),
    );
    queryClient.invalidateQueries({ queryKey: ['locations-bulk-status'] });
    queryClient.invalidateQueries({ queryKey: ['locations-full', selectedFacilityId] });
    onLocationsChange?.();
  }, [selectedFacilityIds, selectedFacilityId, scope, queryClient, onLocationsChange]);

  // handleBulkAddLocations is handled by BulkAddLocationsModal's onSuccess callback
  // eslint-disable-next-line no-unused-vars
  const handleBulkAddLocations = async (names) => {
    if (!selectedFacilityId) return;

    try {
      setIsAdding(true);
      const newLocations = await Promise.all(
        names.map((name) =>
          createLocation({
            facilityId: selectedFacilityId,
            name,
            organizationId,
          }),
        ),
      );

      await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });

      showToast({
        variant: 'success',
        title: 'Success',
        copy: `Areas added successfully. ${newLocations
          .map((loc) => loc.name)
          .join(', ')} have been added`,
      });

      onLocationsChange?.();
    } catch (error) {
      showToast({ variant: 'error', title: 'Error', copy: 'Failed to add areas' });
      throw error;
    } finally {
      setIsAdding(false);
      handleCloseBulkAddModal();
    }
  };

  // Copy FROM modal
  const handleOpenCopyFromModal = () => setShowCopyFromModal(true);
  const handleCloseCopyFromModal = () => setShowCopyFromModal(false);

  const handleCopyFromSource = async (sourceLocationId) => {
    try {
      const { data } = await copyLocationCleaningStandards(sourceLocationId, selectedLocationIds);

      const { totalSucceeded, totalFailed, successfulCopies, failedCopies } = data || {};

      if (totalSucceeded > 0) {
        setLocationsWithStandards((prev) => {
          const updated = new Set(prev);
          (successfulCopies || []).forEach(({ locationId }) => updated.add(locationId));
          return updated;
        });
        await queryClient.refetchQueries({ queryKey: [...LOCATIONS_QUERY_KEY, scope, selectedFacilityId] });
        queryClient.invalidateQueries({ queryKey: ['locations-bulk-status'] });
        queryClient.invalidateQueries({ queryKey: ['locations-full', selectedFacilityId] });
      }

      if (totalFailed === 0 || totalSucceeded > 0) {
        setLastCopyResult(data);
        setShowCopyFromModal(false);
        if (totalSucceeded > 0) {
          const { data: standards } = await getLocationCleaningStandards(sourceLocationId);
          setInitialStandardsForDrawer(standards);
        }
        if (totalFailed === 0) {
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `Standards copied to ${totalSucceeded} area${totalSucceeded !== 1 ? 's' : ''}. Review below and Save to apply.`,
          });
        } else {
          showToast({
            variant: 'error',
            title: 'Partial success',
            copy: `Copied to ${totalSucceeded} of ${data.totalAttempted} areas. ${totalFailed} failed.`,
            duration: 5000,
          });
        }
      } else {
        const firstError = failedCopies?.[0]?.error || 'Copy failed';
        showToast({ variant: 'error', title: 'Error', copy: firstError });
      }
    } catch (error) {
      serviceLogger.error('Failed to copy standards:', error);
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error?.message || 'Failed to copy standards',
      });
    }
  };

  // Get selected locations data for the drawer (from list kept in sync by LocationsSearchList)
  const selectedLocationsData = locationsFromList
    .filter((loc) => selectedLocationIds.includes(loc.id))
    .map((loc) => ({ id: loc.id, name: loc.name }));

  // allLocations for drawer: when drawer is open use full list so "Copy FROM" sees every location; otherwise use paginated list
  const allLocationsForDrawer = useMemo(() => {
    const list = fullLocationsList.length > 0 ? fullLocationsList : locationsFromList;
    return list.map((loc) => {
      const status = cleaningStandardsStatusByLocationId[loc.id] ?? loc.cleaningStandardsStatus;
      const hasStandards =
        status === 'set' || (status === 'assigned' && loc.cleaningStandards);
      return { ...loc, hasStandards };
    });
  }, [
    fullLocationsList,
    locationsFromList,
    cleaningStandardsStatusByLocationId,
  ]);

  // Copy FROM modal: only locations with cleaning standards set (exclude selected targets)
  const configurableLocations = locationsFromList
    .filter((loc) => !selectedLocationIds.includes(loc.id))
    .filter((loc) => locationsWithStandards.has(loc.id))
    .map((loc) => ({
      id: loc.id,
      name: loc.name,
      hasStandards: true,
    }));

  // Bulk actions bar visibility: Step 3 (standards) only when multiple locations selected; Step 2 (crud) when any selected
  const shouldShowBulkActionsBar = useMemo(() => {
    const isDrawerClosed = !showStandardsDrawer;
    const selectedCount = selectedLocationIds.length;
    if (!isDrawerClosed || selectedCount === 0) return false;
    if (readOnlyMode) return selectedCount >= MIN_SELECTED_FOR_BULK_BAR_STANDARDS_MODE;
    return true;
  }, [showStandardsDrawer, selectedLocationIds.length, readOnlyMode]);

  // Empty state when no facility is selected
  if (selectedFacilityIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] px-8 py-10">
        <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center mb-6 max-w-[380px] leading-relaxed">
          Select a facility from the list to manage areas for your organization
        </p>

        <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-5 w-full max-w-[320px]">
          <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-[#333C4D] mb-3">
            You'll be able to:
          </p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Add areas to this facility
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Edit and duplicate areas
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] leading-relaxed">
                Organize your facility structure
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state when multiple facilities are selected
  if (selectedFacilityIds.length > 1 && !readOnlyMode) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] px-8 py-10">
        <div className="bg-gradient-to-br from-[#61b8e7]/10 to-[#61b8e7]/5 border-2 border-[#61b8e7]/30 rounded-2xl p-6 w-full max-w-[500px] mb-6">
          <div className="flex items-start gap-4">
            <div className="bg-[#61b8e7] rounded-lg p-3">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-['Roboto:Bold',sans-serif] font-bold text-[18px] text-[#1a1a1a] mb-2">
                Bulk Area Mode
              </h3>
              <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700 mb-4">
                You have{' '}
                <span className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[#61b8e7]">
                  {selectedFacilityIds.length} facilities
                </span>{' '}
                selected. Add areas to all of them at once!
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {facilities.map((facility) => (
                  <span
                    key={facility.id}
                    className="inline-flex items-center px-2.5 py-1 bg-white border border-[#61b8e7]/30 text-[#61b8e7] rounded-lg text-[13px] font-['Roboto:Medium',sans-serif]"
                  >
                    {facility.name}
                  </span>
                ))}
              </div>

              <button
                type="button"
                onClick={handleOpenBulkAddModal}
                className="w-full bg-[#61b8e7] hover:bg-[#4da3d1] text-white rounded-lg px-6 py-3 font-['Roboto:Bold',sans-serif] text-[15px] transition-all hover:shadow-lg hover:shadow-[#61b8e7]/20 flex items-center justify-center gap-2"
              >
                <Users className="w-5 h-5" />
                Add Areas to {selectedFacilityIds.length} Facilities
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-5 w-full max-w-[500px]">
          <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-[#333C4D] mb-3 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 shrink-0 text-[#61b8e7]" aria-hidden />
            Smart Bulk Add Features:
          </p>
          <div className="space-y-2">
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#333C4D] leading-relaxed">
                Add multiple areas at once
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#333C4D] leading-relaxed">
                Automatic duplicate detection
              </span>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={INFO_CARD_ICON_WRAPPER}>
                <Check className={INFO_CARD_ICON_CLASS} strokeWidth={4} aria-hidden />
              </div>
              <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#333C4D] leading-relaxed">
                Preview changes before applying
              </span>
            </div>
          </div>
        </div>

        <BulkAddLocationsModal
          isOpen={showBulkAddModal}
          onClose={handleCloseBulkAddModal}
          facilities={facilities}
          selectedFacilityIds={selectedFacilityIds}
          organizationId={organizationId}
          onSuccess={handleBulkAddSuccess}
        />
      </div>
    );
  }

  // Read-only mode with multiple facilities
  if (selectedFacilityIds.length > 1 && readOnlyMode) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] px-8 py-10">
        <p className="font-['Roboto:Regular',sans-serif] text-[15px] text-[#6e6e71] text-center mb-6 max-w-[380px] leading-relaxed">
          Select only{' '}
          <span className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[#1a1a1a]">
            one facility
          </span>{' '}
          to view its areas
        </p>

        <div className="bg-[#fff5e6] rounded-lg p-5 border border-[#ffd699] w-full max-w-[320px]">
          <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#8b6914] leading-relaxed">
            You currently have{' '}
            <span className="font-['Roboto:SemiBold',sans-serif] font-semibold">
              {selectedFacilityIds.length} facilities
            </span>{' '}
            selected. Areas are managed individually per facility.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <LocationsSearchList
        selectedFacilityId={selectedFacilityId}
        queryScope={scope}
        selectedLocationIds={selectedLocationIds}
        onSelectLocation={handleSelectLocation}
        onSelectAll={handleSelectAllLocations}
        onUpdateLocation={handleUpdateLocation}
        onDuplicateLocation={handleDuplicateLocation}
        onOpenStandards={handleOpenIndividualStandards}
        showCleaningStandards={showCleaningStandards}
        readOnlyMode={readOnlyMode}
        locationsWithStandards={locationsWithStandards}
        cleaningStandardsStatusByLocationId={cleaningStandardsStatusByLocationId}
        onLocationsChange={handleLocationsChange}
      />

      {/* Add New Location */}
      {!readOnlyMode && <AddLocationInline onAdd={handleAddLocation} isAdding={isAdding} />}

      {/* Bulk Actions Bar: when drawer is closed; in Step 3 (standards) only when multiple locations selected */}
      {shouldShowBulkActionsBar && (
        <BulkActionsBar
          selectedCount={selectedLocationIds.length}
          onDelete={readOnlyMode ? undefined : handleOpenDeleteModal}
          onSetStandards={handleOpenStandardsDrawer}
          onCopyFrom={handleOpenCopyFromModal}
          onClear={handleClearSelection}
          isDeleting={isDeleting}
          mode={readOnlyMode ? 'standards' : 'crud'}
        />
      )}

      {/* Delete Confirmation Modal - same layout as DeleteFacilityModal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleDeleteLocations}
        count={selectedLocationIds.length}
        locationNames={selectedLocationsData.map((loc) => loc.name)}
      />

      {/* Cleaning Standards Drawer */}
      <CleaningStandardsDrawer
        isOpen={showStandardsDrawer}
        onClose={handleCloseIndividualStandards}
        locations={selectedLocationsData}
        onSave={handleSaveStandards}
        onSaveDraft={handleSaveDraft}
        copyResult={lastCopyResult}
        initialStandards={initialStandardsForDrawer}
        onInitialStandardsApplied={() => setInitialStandardsForDrawer(null)}
        allLocations={allLocationsForDrawer}
        onCopyFrom={() => {
          // Keep drawer open - modal will appear on top
          setShowCopyFromModal(true);
        }}
        onCopyToLocations={(targetIds, standards) => {
          serviceLogger.log('Copying to locations:', targetIds, standards);
          showToast({
            variant: 'success',
            title: 'Success',
            copy: `Standards copied to ${targetIds.length} area${
              targetIds.length !== 1 ? 's' : ''
            }`,
          });
        }}
      />

      {/* Copy FROM Location Modal */}
      <CopyFromLocationModal
        isOpen={showCopyFromModal}
        onClose={handleCloseCopyFromModal}
        onConfirm={handleCopyFromSource}
        targetLocations={selectedLocationsData}
        availableSources={configurableLocations}
      />

      {/* Duplicate Location Modal */}
      {locationToDuplicate && (
        <DuplicateLocationModal
          isOpen={isDuplicateModalOpen}
          onClose={handleCloseDuplicateModal}
          onConfirm={handleConfirmDuplicateLocation}
          location={locationToDuplicate}
          hasStandards={locationsWithStandards.has(locationToDuplicate.id)}
        />
      )}
    </div>
  );
};
